import { Injectable, inject } from '@angular/core';
import { CoreApiService } from '../core-api-service';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { API_URLS, initialPagination } from 'app/modules/core/constants/core.constants';
import {
  ClientDraftDTO,
  ClientDraftInsertDTO,
  ClientDraftListDTO,
  PolicyType,
  ClientDraftUpdateDTO,
  DraftType,
  HealthClaimDTO
} from 'models/DTOs';
import { UserService } from 'app/services/user.service';
import {
  HealthQuoteInsertDTO,
  HomeQuoteDTO,
  MotorQuoteDTO,
  DraftQuote,
  HomeQuotePages
} from 'app/modules/quotes/models/models';
import {
  DraftClaim,
  HomeClaimDTO,
  HomeClaimPages,
  MotorClaimDTO,
  MotorClaimPages
} from 'app/modules/claims/models/models';
import { NotificationService } from '@services/notification.service';
import { PagingParams } from 'app/models/BaseSearch/PagingParams';

export interface DraftList {
  list: ClientDraftListDTO[];
  maxCount: number;
}

@Injectable({
  providedIn: 'root'
})
export class DraftService extends CoreApiService {
  private _userService: UserService = inject(UserService);
  private _notifications: NotificationService = inject(NotificationService);

  draftSubject: BehaviorSubject<DraftList> = new BehaviorSubject({ list: [], maxCount: 0 });
  private currentPagination: PagingParams = Object.assign({}, initialPagination);

  saveQuoteDraft(saveQuote: DraftQuote, clientPolicyQuoteID?: string): Observable<ClientDraftDTO | any> {
    const user = this._userService.userSubject.value;
    if (user && saveQuote?.quote) {
      const draft: Partial<ClientDraftInsertDTO | ClientDraftUpdateDTO> = {
        clientID: user.clientID,
        subject: '',
        policyType: saveQuote.type,
        draftType: DraftType.Quote,
        description1: '',
        description2: '',
        description3: '',
        data: JSON.stringify(saveQuote.quote),
        signatureUrl: saveQuote?.signatureUrl,
        isDeleted: saveQuote?.isDeleted,
        isSigned: saveQuote?.isSigned,
        isSubmited: saveQuote?.isSubmited
      };

      switch (saveQuote.type) {
        case PolicyType.Home:
          const home = saveQuote.quote as HomeQuoteDTO;
          draft.subject = home.pageData[HomeQuotePages.CONFIRM_OWNER_DETAILS]?.data.ownerFullName;
          const { propertyName, propertyAddress, district, island } = home.pageData[HomeQuotePages.HOME_ADDRESS]?.data || {};
          draft.description1 = `${propertyName}: ${propertyAddress}, ${district}, ${island}`;
          break;

        case PolicyType.Motor:
          const motor = saveQuote.quote as MotorQuoteDTO;
          draft.subject = motor.fullName;
          draft.description1 = '';
          motor.vehicles?.forEach(
            item => (draft.description1 += `${item.vehicleYear} ${item.vehicleMake} ${item.vehicleModel} •`)
          );
          break;

        case PolicyType.Health:
          const health = saveQuote.quote as HealthQuoteInsertDTO;
          draft.subject = `${health.applicantFirstName} ${health.applicantLastName}`;
          draft.description1 = String(health.applicantAddress);
          draft.description2 = health.applicantEmail;
          break;
      }

      const request: Observable<ClientDraftInsertDTO | any> = !clientPolicyQuoteID
        ? this.post<ClientDraftInsertDTO>(API_URLS.clientDrafts, draft)
        : this.put<any>(`${API_URLS.clientDrafts}/${clientPolicyQuoteID}`, draft);
      return request.pipe(
        tap(() => {
          this._notifications.showMessage('Quote has been saved successfully');
        })
      );
    }
  }

  saveClaimDraft(saveClaim: DraftClaim, draftId?: string): Observable<ClientDraftDTO | any> {
    const user = this._userService.userSubject.value;
    if (user && saveClaim?.claim) {
      const draft: Partial<ClientDraftInsertDTO | ClientDraftUpdateDTO> = {
        clientID: user.clientID,
        subject: '',
        policyType: saveClaim.type,
        draftType: DraftType.Claim,
        description1: '',
        description2: '',
        description3: '',
        data: JSON.stringify(saveClaim.claim),
        signatureUrl: saveClaim?.signatureUrl,
        isDeleted: saveClaim?.isDeleted,
        isSigned: saveClaim?.isSigned,
        isSubmited: saveClaim?.isSubmited
      };

      switch (saveClaim.type) {
        case PolicyType.Home:
          const home = saveClaim.claim as HomeClaimDTO;
          draft.subject = home.pageData[HomeClaimPages.RISK_LOCATION]?.data.address;
          draft.description1 = home.pageData[HomeClaimPages.RISK_LOCATION]?.data.riskDescription;
          break;

        case PolicyType.Motor:
          const motor = saveClaim.claim as MotorClaimDTO;
          draft.subject = `${motor.pageData[MotorClaimPages.VEHICLE]?.data.selectedVehicle.model}, ${motor.pageData[MotorClaimPages.VEHICLE]?.data.selectedVehicle.registrationNumber}`;
          draft.description1 = motor.pageData[MotorClaimPages.LOCATION]?.data.location;
          break;

        case PolicyType.Health:
          const health = saveClaim.claim as HealthClaimDTO;
          draft.subject = `${health[0]?.patient?.firstName} ${health[0]?.patient?.firstName}`;
          draft.description1 = String(health[0]?.patient?.patientsAddress);
          break;
      }

      const request: Observable<ClientDraftInsertDTO | any> = !draftId
        ? this.post<ClientDraftInsertDTO>(API_URLS.clientDrafts, draft)
        : this.put<any>(`${API_URLS.clientDrafts}/${draftId}`, draft);
      return request.pipe(
        tap(() => {
          this._notifications.showMessage('Claim has been saved successfully');
        })
      );
    }
  }

  getDraftCount() {
    const user = this._userService.userSubject.value;
    if (user) {
      return this.get<number>(API_URLS.clientDraftsCount + '/' + user.clientID);
    }
  }

  getDraftsList(reload: boolean) {
    const user = this._userService.userSubject.value;

    if (user) {
      reload ? this.currentPagination.pageNumber++ : (this.currentPagination = Object.assign({}, initialPagination));
      const params: any = {
        clientId: user.clientID,
        ...this.currentPagination
      };

      this.get<ClientDraftListDTO[]>(API_URLS.clientDrafts, { params: { ...params } }).subscribe(response => {
        if (!response || (response.length === 0 && this.currentPagination.pageNumber > 1)) {
          this.currentPagination.pageNumber--;
        }
        const draftList: ClientDraftListDTO[] = [...(reload ? this.draftSubject.value.list : []), ...response];
        this.draftSubject.next({ list: draftList, maxCount: draftList.length });
      });
    }
  }

  getSavedDraft(draftId: string) {
    return this.get<ClientDraftDTO>(`${API_URLS.clientDrafts}/${draftId}`);
  }

  deleteSavedDraft(draftId: string) {
    this.delete<string>(`${API_URLS.clientDrafts}/${draftId}`).subscribe(() => {
      this.getDraftsList(false);
    });
  }
}
